/* eslint-disable jsx-a11y/label-has-associated-control */
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAutocomplete } from '@mui/material';
import { useConfig } from '@revenuewell/logic-config';
import { useOidc, useOidcWithAdmin } from '@revenuewell/logic-oidc';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { useHistory } from 'apps/wrapper/src/hooks/use-history-intercept';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import InboxService from 'apps/wrapper/src/services/inbox-service/inbox-service';
import { AllChannelsUnreadCounts } from 'apps/wrapper/src/services/inbox-service/types';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Listbox } from './location-selection.styles';
import { LocationItem } from './locationItem';

// channel-update fires for every text, call and inbox action at any of the user's locations.
// Debounce refetches: a burst of events collapses into one call. maxWait guarantees a refetch at least
// every 60 seconds even while events keep arriving. Dropdown counts are not time-critical.
const UNREAD_REFETCH_DEBOUNCE_MS = 60 * 1000;

export const LocationSelection = () => {
  const config = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { listen: listenNotifications } = useNotifications();
  const { locations, selectedLocationIds, switchLocation } = useLocations();
  const { getClaims } = useOidc();
  const [locationUnreads, setLocationUnreads] = useState<AllChannelsUnreadCounts>({});
  const inboxService = useMemo(() => InboxService.getInstance(config, oidcService), [config, oidcService]);
  const { queryIntercepted } = useHistory();
  const customerId = queryIntercepted.current.get('customerId');

  const fetchUnreadMessages = useCallback(async () => {
    // The dropdown only renders for 2+ locations, and an empty list (still loading)
    // makes the backend query every mailbox in the account. Skip both cases.
    if (locations.length <= 1) return;

    const claims = await getClaims();

    if (!claims.masterAccountId && !customerId) return;

    const allUnread = await inboxService.allChannelsUnreadCounts({
      masterAccountId: claims.masterAccountId || customerId,
      locationIds: locations.map((location) => location.id)
    });

    setLocationUnreads(allUnread);
  }, [inboxService, locations, customerId]);

  // Keep the debounced function stable across renders while always calling the latest fetch.
  const fetchUnreadMessagesRef = useRef(fetchUnreadMessages);
  fetchUnreadMessagesRef.current = fetchUnreadMessages;

  const debouncedFetchUnreadMessages = useMemo(
    () =>
      debounce(() => fetchUnreadMessagesRef.current(), UNREAD_REFETCH_DEBOUNCE_MS, {
        maxWait: UNREAD_REFETCH_DEBOUNCE_MS
      }),
    []
  );

  useEffect(() => () => debouncedFetchUnreadMessages.cancel(), [debouncedFetchUnreadMessages]);

  useEffect(() => {
    // Initial load (and location list changes) fetch immediately; notifications are debounced.
    fetchUnreadMessages();
  }, [fetchUnreadMessages]);

  useEffect(() => {
    if (locations.length <= 1) return;

    return listenNotifications('channel-update', () => {
      debouncedFetchUnreadMessages();
    });
  }, [listenNotifications, debouncedFetchUnreadMessages, locations.length]);

  const locationsSorted = useMemo(() => {
    const selectedLocation = locations.filter((location) => selectedLocationIds.includes(location.id));

    const unselectedLocations = locations
      .filter((location) => !selectedLocationIds.includes(location.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...selectedLocation, ...unselectedLocations];
  }, [locations, selectedLocationIds]);

  const selectedLocation = locations.find((location) => location.id === selectedLocationIds[0]);

  const { getRootProps, getInputLabelProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } =
    useAutocomplete({
      id: 'location-selection',
      options: locationsSorted,
      value: selectedLocation,
      onChange: (_, newValue) => {
        if (newValue) switchLocation(newValue.id);
      },
      getOptionLabel: (option) => option.name
    });

  const inputProps = getInputProps();

  if (locations.length <= 1) return null;

  return (
    <Container>
      <div {...getRootProps()}>
        <label {...getInputLabelProps()}>Location</label>
        <input {...inputProps} value={inputProps.value || selectedLocation?.name} title={selectedLocation?.name} />
        <KeyboardArrowDownIcon />
      </div>
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()}>
          {(groupedOptions as typeof locations).map((option, index) => {
            const { key, ...optionProps } = getOptionProps({ option, index }) as any;

            return <LocationItem key={key} option={option} unreadCount={locationUnreads[option.id]} {...optionProps} />;
          })}
        </Listbox>
      ) : null}
    </Container>
  );
};
